import api from 'api/api'
import queryClient from 'api/queryClient'
import { getAnswerBatched } from 'api/answerBatchFetch'
import { getCachedAnswer, getCachedQuest } from 'state/cache'
import { shouldReconcileOnSubscribe } from './channelReconcilePolicy'

type ChannelEntity = 'answer' | 'quest'

const inFlightReconciles: Record<ChannelEntity, Map<string, Promise<unknown>>> = {
    answer: new Map(),
    quest: new Map(),
}
const lastReconcileAttemptAt: Record<ChannelEntity, Map<string, number>> = {
    answer: new Map(),
    quest: new Map(),
}
// fetchFresh writes to the ENTITY cache, not the react-query query state, so a reconcile that
// finds no changes never advances dataUpdatedAt — leaving the entity permanently "stale" and
// re-fetching on every row mount (throttled only by the 10s retry window). Virtualized
// scrolling mounts rows continuously, so without this stamp a scroll session issues one full
// fetch per row per 10s, forever.
const lastReconcileSuccessAt: Record<ChannelEntity, Map<string, number>> = {
    answer: new Map(),
    quest: new Map(),
}

function queryKeyFor(entity: ChannelEntity, id: string) {
    return [entity, id]
}

function isLocalOnlyAnswer(answer: any) {
    return !!answer && (answer.is_draft || answer.isLocal || answer.isLocalDraft)
}

function isLocalOnlyQuest(quest: any) {
    return (
        !!quest &&
        (quest.is_draft ||
            quest.localChildQuest ||
            quest.isLocalChildQuest ||
            quest.isLocalRootQuest)
    )
}

function reconcileEntity({
    entity,
    id,
    hasCacheEntry,
    isLocalOnly,
    fetchFresh,
}: {
    entity: ChannelEntity
    id: string | number | null | undefined
    hasCacheEntry: boolean
    isLocalOnly: boolean
    fetchFresh: () => Promise<unknown>
}) {
    if (!id) return Promise.resolve(null)

    const normalizedId = String(id)
    const now = Date.now()
    const lastAttemptAt = lastReconcileAttemptAt[entity].get(normalizedId) || 0
    const dataUpdatedAt = Math.max(
        queryClient.getQueryState(queryKeyFor(entity, normalizedId))?.dataUpdatedAt || 0,
        lastReconcileSuccessAt[entity].get(normalizedId) || 0,
    )

    if (
        !shouldReconcileOnSubscribe({
            now,
            dataUpdatedAt,
            lastAttemptAt,
            hasCacheEntry,
            isLocalOnly,
        })
    ) {
        return Promise.resolve(null)
    }

    const existing = inFlightReconciles[entity].get(normalizedId)
    if (existing) return existing

    lastReconcileAttemptAt[entity].set(normalizedId, now)
    const request = fetchFresh()
        .then(result => {
            if (result != null) {
                lastReconcileSuccessAt[entity].set(normalizedId, Date.now())
            }
            return result
        })
        .catch(() => null)
        .finally(() => {
            inFlightReconciles[entity].delete(normalizedId)
        })

    inFlightReconciles[entity].set(normalizedId, request)
    return request
}

export function reconcileAnswerOnSubscribe(answerId: string | number | null | undefined) {
    if (!answerId) return Promise.resolve(null)

    const id = String(answerId)
    const cachedAnswer = getCachedAnswer(id)

    return reconcileEntity({
        entity: 'answer',
        id,
        hasCacheEntry: !!cachedAnswer,
        isLocalOnly: isLocalOnlyAnswer(cachedAnswer),
        fetchFresh: async () => {
            // Subscriptions come up in bursts while scrolling; coalesce their
            // reconcile fetches into one bulk call (caches as a side effect).
            const answer = await getAnswerBatched(id)
            return answer || null
        },
    })
}

export function reconcileQuestOnSubscribe(questId: string | number | null | undefined) {
    if (!questId) return Promise.resolve(null)

    const id = String(questId)
    const cachedQuest = getCachedQuest(id)

    return reconcileEntity({
        entity: 'quest',
        id,
        hasCacheEntry: !!cachedQuest,
        isLocalOnly: isLocalOnlyQuest(cachedQuest),
        fetchFresh: () => api.getQuestQuery(id),
    })
}
